import React, { useState } from 'react';
import { Container, Card, Button, Form, Alert } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../AuthContext';
import '../styles/pages/ContactUs.css';
import useApi from '../Api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { validateTextInput } from '../components/common/TextValidator';

const ContactUs = () => {
  const { user, token } = useAuth();
  const [feedback, setFeedback] = useState('');
  const [errors, setErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const apiClient = useApi();
  const navigate = useNavigate();

  const handleFeedbackChange = (e) => {
    const newFeedback = e.target.value;
    setFeedback(newFeedback);
    const { errors: validationErrors } = validateTextInput(newFeedback, {
      maxLength: 1000,
      minLength: 1,
      field_name: "Feedback"
    });
    setErrors(validationErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const { isValid, errors: validationErrors, sanitizedText } = validateTextInput(feedback, {
      maxLength: 1000,
      minLength: 1,
      field_name: "Feedback"
    });
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await apiClient.post('/feedback', { feedback: sanitizedText }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 201) {
        toast.success('Thank you for your feedback!', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        setFeedback('');
        setErrors([]);
      } else {
        toast.error('Failed to submit feedback. Please try again later.', {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      const errorMessage = error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : 'An error occurred while submitting your feedback.';
      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="contactus-container">
      <Helmet>
        <title>Contact Us - WeiFinders</title>
        <meta name="description" content="Get in touch with WeiFinders for support, feedback, or inquiries." />
        <meta property="og:title" content="Contact Us - WeiFinders" />
        <meta property="og:description" content="Get in touch with WeiFinders for support, feedback, or inquiries." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://weifinders.com/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@WeiFinders" />
        <meta name="twitter:title" content="Contact Us - WeiFinders" />
        <meta name="twitter:description" content="Get in touch with WeiFinders for support, feedback, or inquiries." />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "Contact Us - WeiFinders",
            "url": "https://weifinders.com/contact"
          }
        `}</script>
      </Helmet>
      <Card className="contactus-card">
        <Card.Body>
          <h2 className="contactus-title">Contact Us</h2>
          <p className="contactus-info">
            <strong>Email:</strong> <a href="mailto:support@WeiFinders.com">support@WeiFinders.com</a><br />
          </p>
          {user ? (
            <Form onSubmit={handleSubmit} role="form">
              <Form.Group controlId="feedback">
                <Form.Label>Your Feedback</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={feedback}
                  onChange={handleFeedbackChange}
                  maxLength={1000}
                  placeholder="Enter your queries, feedback, suggestions, or issues here..."
                  aria-label="Feedback text area"
                />
                <Form.Text muted>
                  {feedback.length}/1000 characters
                </Form.Text>
              </Form.Group>
              {errors.length > 0 && (
                <Alert variant="danger" className="mt-3 mx-auto" style={{ width: 'fit-content' }}>
                  {errors.map((error, idx) => (
                    <div key={idx}>{error}</div>
                  ))}
                </Alert>
              )}
              <Button variant="primary" type="submit" className="mt-3" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </Form>
          ) : (
            <p className="login-message">
              Please login to your account to share via this page.
            </p>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ContactUs;
